import React, { memo, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import cuid from 'cuid';
import { get } from 'lodash-es';

import SortableImage from './SortableImage';
import getLotExtension from '../Step4/getLotExtension';
import { imagePath, isProd } from '../../../utilities/UrlPaths';
import LotImageErrored from './LotImageErrored';
import LotImageUploading from './LotImageUploading';

// 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const NO_IMAGE = `${imagePath}/images/NoImage.png`;

// Default Axios requests to retry 3 additional times if they fail
axiosRetry(axios, {
  retries: 3,
  retryDelay: () => 3000
});

/**
 * One Add/Edit-lot image tile. Addressed by the parent through its list `index`; the parent mounts
 * this only once the file is admitted (drop) or already uploaded (sortable), so mount === upload
 * start for the uploading case.
 *
 * Every terminal XHR outcome now settles the wrapping promise (COR-3192): the S3 PUT previously had
 * no `onerror` handler and a non-rejecting `ontimeout`, so a network failure left the promise
 * dangling and the file stuck "uploading" forever. Here load (status-guarded) / error / abort /
 * timeout each resolve or reject exactly once, and the single `catch` maps to one terminal report.
 */
const AddLotImage = ({
  abortFile,
  addFileError,
  catalogID,
  file,
  handleDeleteFile,
  handleFileComplete,
  handleImageSizeCheck,
  houseID,
  imageUploaded,
  imageUploadedSrc,
  index,
  lotData,
  lotNumber,
  maxSeqNumber
}) => {
  const [progress, setProgress] = useState(0);
  const [imageThumbnail, setImageThumbnail] = useState(null);
  const [imageS3URL, setImageS3URL] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Cancel token for the CPS request; raw XHR for the S3 PUT (progress + timeout control).
  const cancelTokenRef = useRef(null);
  const xhrRef = useRef(null);
  const remainingAwsRetriesRef = useRef(5);

  const hasError = get(file, 'hasError', false);

  // Cancel the in-flight XHR + CPS request (user cancel, or unmount).
  const cancelUpload = () => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
  };

  /**
   * Wrap the S3 PUT (a raw XHR, for progress + timeout control) in a promise that settles on
   * EVERY terminal outcome. `ontimeout` rejects with 'retry' while retries remain (the caller
   * re-runs) or 'timeout' once exhausted — it never leaves the promise dangling.
   */
  const handlePutRequest = putUrl =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      // Timeout after 5 minutes
      xhr.timeout = 1000 * 60 * 5;
      xhr.open('PUT', putUrl);
      xhr.setRequestHeader('Content-Type', file.type);

      // NOTE: for a PUT this is xhr.upload.onprogress, NOT xhr.onprogress.
      xhr.upload.onprogress = progressEvent => {
        const { loaded, total } = progressEvent;
        setProgress(Math.round((loaded / total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const err = new Error('default');
          err.status = xhr.status;
          reject(err);
        }
      };

      // Was missing entirely — a network-level failure now rejects instead of hanging.
      xhr.onerror = () => reject(new Error('network'));
      xhr.onabort = () => reject(new Error('abort'));
      xhr.ontimeout = () => {
        if (remainingAwsRetriesRef.current > 0) {
          remainingAwsRetriesRef.current -= 1;
          reject(new Error('retry'));
        } else {
          reject(new Error('timeout'));
        }
      };

      xhr.send(file);
    });

  const uploadFile = async (imageSeq, lotExt, lotNum) => {
    try {
      // Safari caches GETs; a unique nocache token keeps each presign request distinct.
      const uniqueIdentifier = cuid();
      const s3BucketUrl = `https://9ctqzg3xxj.execute-api.us-east-1.amazonaws.com/${
        isProd ? 'prod' : 'stage'
      }/INV-ImageUploader?numImages=0&bucket=/house/${houseID}/catalog/${catalogID}&nocache=${uniqueIdentifier}`;

      const awsUrlResponse = await axios(s3BucketUrl, {
        'axios-retry': { retries: 3, retryDelay: () => 3000 }
      });

      const imageURL = get(awsUrlResponse, 'data[0].imageUrl', '');
      const awsPutUrl = get(awsUrlResponse, 'data[0].putUrl', '');

      // Step 2 - PUT the file to S3
      await handlePutRequest(awsPutUrl);

      // Step 3 - PUT to CPS
      const cpsPutUrl = `/cps/app/image/house/${houseID}/catalog/${catalogID}?fromDIY=true&doValidation=false`;
      const cpsPutData = [
        {
          imageSeq,
          imageURL,
          lotExt: lotData.lotExt,
          lotNum: lotData.lotNumber,
          invNumber: lotData.invNumber
        }
      ];

      cancelTokenRef.current = axios.CancelToken.source();

      const cpsPutResponse = await axios.put(cpsPutUrl, cpsPutData, {
        cancelToken: cancelTokenRef.current.token,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });

      const cpsPutResponseError = get(cpsPutResponse, 'data.data.images[0].error', null);
      if (!cpsPutResponseError) {
        const s3Url = get(cpsPutResponse, 'data.data.images[0].imageS3URL', null);
        // Signal file is complete so the parent moves it into the uploaded list.
        handleFileComplete(s3Url, imageSeq);
        setImageS3URL(s3Url);
        setIsUploading(false);
      } else {
        addFileError(cpsPutResponseError === 'Invalid Image' ? 'invalidImage' : 'timeout', index);
        setIsUploading(false);
      }
    } catch (e) {
      if (e.message === 'abort' || axios.isCancel(e)) {
        // Withdrawn/cancelled — remove the file, no error to surface.
        abortFile(index);
        return;
      }
      if (e.message === 'retry') {
        // Timeout with retries remaining — re-run after the standard delay.
        setTimeout(() => uploadFile(imageSeq, lotExt, lotNum), 3000);
        return;
      }
      // A network error / axios failure sends no status — treat as a connection loss.
      let errorMsg = e.message;
      if (!e.status) {
        errorMsg = 'timeout';
      }
      addFileError(errorMsg, index);
      setIsUploading(false);
    }
  };

  const uploadNewFile = () => {
    // Step 1 - If the file already has an error (rejected by dropzone) - nothing to upload.
    if (get(file, 'hasError', false)) {
      return;
    }

    // Step 2 - Test file size
    if (file.size > MAX_FILE_SIZE) {
      addFileError('size', index);
      setIsUploading(false);
      return;
    }

    // Step 3 - Small-image check: allows sub-1000px images but flags the warning banner.
    try {
      let smallImageCheck = false;
      const imageTest = new Image();
      imageTest.src = URL.createObjectURL(file);
      imageTest.onload = () => {
        if (imageTest.naturalWidth < 1000 || imageTest.naturalHeight < 1000) {
          smallImageCheck = true;
        }
        handleImageSizeCheck(smallImageCheck);
      };
    } catch (e) {
      console.log(e); // eslint-disable-line no-console
    }

    // Load the thumbnail and combine the upload 'start' to reduce the total number of renders.
    const thumbnail = URL.createObjectURL(file);
    setIsUploading(true);
    setImageThumbnail(thumbnail);

    /**
     * In this case (Add/Edit) we just want to add the new images to the end.
     * If the image is the first one - imageSeq should be 0.
     * Otherwise get the max sequence number which already exists, add the index and +1.
     */
    const imageSeq = index === 0 && maxSeqNumber === -1 ? 0 : 1 + index + maxSeqNumber;

    const { lotNumber: lotNum, lotExt } = getLotExtension(lotNumber);

    uploadFile(imageSeq, lotExt, lotNum);
  };

  // Upload once on mount. Already-uploaded (sortable) tiles pass imageUploaded and never upload.
  // Abort any in-flight request on unmount.
  useEffect(() => {
    if (!imageUploaded) {
      uploadNewFile();
    }
    return () => cancelUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let imageSrc;
  const errorCode = get(file, 'error.code', null);
  if (imageUploaded && imageUploadedSrc) {
    // Already uploaded - point at the S3 thumbnail.
    imageSrc = imageUploadedSrc;
  } else if (errorCode === 'type' || errorCode === 'timeout') {
    // Invalid type / timeout: skip holding a thumbnail in memory, show the placeholder.
    imageSrc = NO_IMAGE;
  } else if (imageS3URL) {
    imageSrc = imageS3URL;
  } else {
    imageSrc = imageThumbnail || NO_IMAGE;
  }

  // Set the appropriate on click handler for the cancel upload / delete button.
  let cancelDeleteOnClick;
  if (progress === 0) {
    cancelDeleteOnClick = () => abortFile(index);
  } else if (isUploading) {
    cancelDeleteOnClick = () => cancelUpload();
  } else {
    cancelDeleteOnClick = () => handleDeleteFile(index);
  }

  /**
   * Since the sortable container must be wrapped in a containing element, we separate un-sortable
   * files. First a row of errored files, then the row of uploaded files, finally the row of files
   * still uploading. See AddLotImageContainer.jsx.
   */

  // Has to be separated out from the sortable container.
  if (hasError) {
    return (
      <LotImageErrored
        handleDeleteFile={abortFile}
        errorDescription={file.error.description}
        fileName={file.name}
        imageSrc={imageSrc}
        index={index}
      />
    );
  }

  // All uploaded images which are now sortable.
  if (imageUploaded) {
    // (dyouberg) Index is required by react-sortable-hoc but is used internally
    return (
      <SortableImage
        handleDeleteFile={handleDeleteFile}
        image={imageSrc}
        imageNumber={index}
        index={index}
      />
    );
  }

  // The images which are still uploading...
  return (
    <LotImageUploading
      cancelDeleteOnClick={cancelDeleteOnClick}
      fileName={file.name}
      imageSrc={imageSrc}
      progress={progress}
    />
  );
};

/**
 * Shallow-prop memo replaces the old getDerivedStateFromProps + shouldComponentUpdate isEqual
 * guard. The parent passes stable (useCallback) callbacks and preserves unchanged file references,
 * so completing/erroring one file does not re-render its siblings.
 */
export default memo(AddLotImage);
